import type { Module } from "vuex";
import { PLATFORM } from "hoxton-shared/enums/exports";

import { NotificationStatus, NotificationTypes } from "@/enums/notifications";
import type { JobNotification } from "@/store/modules/jobs";

import type { RootState } from "@/store/types";

const storeNamespace = "notifications";

export const NotificationsMutation = {
    Add: "addItem",
    AddDownloadLinkToNotification: "addDownloadLinkToNotification",
    ClearItemsNotPendingOrInProgress: "clearItemsNotPendingOrInProgress",
    ClearCompletedItems: "clearCompletedItems",
    IncrementIdCounter: "incrementIdCounter",
    RemoveItem: "removeItem",
    SetDrawerIsVisible: "setDrawerIsVisible",
    Update: "updateItem"
};

export const NotificationsAction = {
    Add: `${storeNamespace}/${NotificationsMutation.Add}`,
    AddDownloadLinkToNotification: `${storeNamespace}/${NotificationsMutation.AddDownloadLinkToNotification}`,
    ClearItemsNotPendingOrInProgress: `${storeNamespace}/${NotificationsMutation.ClearItemsNotPendingOrInProgress}`,
    ClearCompletedItems: `${storeNamespace}/${NotificationsMutation.ClearCompletedItems}`,
    RemoveItem: `${storeNamespace}/${NotificationsMutation.RemoveItem}`,
    SetDrawerIsVisible: `${storeNamespace}/${NotificationsMutation.SetDrawerIsVisible}`,
    Update: `${storeNamespace}/${NotificationsMutation.Update}`
};

export const NotificationsGetter = {
    GetItemById: `${storeNamespace}/getItemById`
};

export type State = {
    drawerIsVisible: boolean;
    idCounter: number;
    items: Notification[];
};

export type Notification = {
    id: number;
    name?: string;
    status: (typeof NotificationStatus)[keyof typeof NotificationStatus];
    message: string;
    downloadLink?: string;
    type: (typeof NotificationTypes)[keyof typeof NotificationTypes];
    job: JobNotification;
    platform?: (typeof PLATFORM)[keyof typeof PLATFORM];
    deliverablesCount?: number;
};

const getDefaultState = (): State => ({
    drawerIsVisible: false,
    idCounter: 0,
    items: []
});

const notificationsModule: Module<State, RootState> = {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        getItemById(state) {
            return (itemId: number) => state.items.find(item => item.id === itemId);
        }
    },

    mutations: {
        addItem: (state, payload: State["items"][number]) => {
            state.items.unshift(payload);
        },

        clearItemsNotPendingOrInProgress(state) {
            state.items = state.items.filter(
                item => item.status === NotificationStatus.Pending || item.status === NotificationStatus.InProgress
            );
        },

        clearCompletedItems(state) {
            state.items = state.items.filter(item => item.status !== NotificationStatus.Completed);
        },

        incrementIdCounter(state) {
            state.idCounter += 1;
        },

        removeItem(state, itemId) {
            state.items = state.items.filter(item => item.id !== itemId);
        },

        setDrawerIsVisible(state, drawerIsVisible: State["drawerIsVisible"]) {
            state.drawerIsVisible = drawerIsVisible;
        },

        updateItem(state, updatedNotification: State["items"][number]) {
            state.items = state.items.map(item => {
                if (item.id === updatedNotification.id) {
                    return {
                        ...item,
                        ...updatedNotification
                    };
                }

                return item;
            });
        }
    },

    actions: {
        addDownloadLinkToNotification({ commit }, downloadLink: string) {
            commit(NotificationsMutation.AddDownloadLinkToNotification, downloadLink);
        },

        addItem({ commit, state }, notification: Omit<State["items"][number], "id">) {
            commit(NotificationsMutation.IncrementIdCounter);
            commit(NotificationsMutation.Add, {
                ...notification,
                id: state.idCounter
            });

            return state.idCounter;
        },

        clearItemsNotPendingOrInProgress({ commit }) {
            commit(NotificationsMutation.ClearItemsNotPendingOrInProgress);
        },

        removeItem({ commit }, itemId: number) {
            commit(NotificationsMutation.RemoveItem, itemId);
        },

        setDrawerIsVisible({ commit }, drawerIsVisible: State["drawerIsVisible"]) {
            commit(NotificationsMutation.SetDrawerIsVisible, drawerIsVisible);
        },

        updateItem({ commit }, updatedNotification: State["items"][number]) {
            commit(NotificationsMutation.Update, updatedNotification);
        },

        clearCompletedItems({ commit }) {
            commit(NotificationsMutation.ClearCompletedItems);
        }
    }
};

export default notificationsModule;
